import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, WifiHigh, Lightning, CurrencyDollar, Headset, Wrench,
  Package, CheckCircle, Warning, Clock, Globe, ChartLineUp,
  UserPlus, Prohibit, CreditCard, Path, ShieldCheck, ArrowRight,
  ClipboardText, CaretRight
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ['#0052CC', '#006D3B', '#7C4DFF', '#F57F17', '#BA1A1A', '#00838F', '#AD1457', '#FF6D00'];
const STATUS_CLR = { New: '#0D47A1', Scheduled: '#00695C', Assigned: '#4527A0', 'In Progress': '#283593', Completed: '#1B5E20', Resolved: '#1B5E20', Cancelled: '#B71C1C', 'Pending Approval': '#F57F17', Pending: '#F57F17', Closed: '#37474F' };
const TICKET_LIFECYCLE_STATUSES = ['New', 'Scheduled', 'Pending', 'Assigned', 'In Progress', 'Resolved', 'Closed', 'Cancelled'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dmaHealth, setDmaHealth] = useState(null);

  // Only Admin/SuperAdmin can access Dashboard
  useEffect(() => {
    if (user && !['Admin', 'SuperAdmin'].includes(user.role)) {
      const ROLE_DEFAULT = { Operator: '/operator', IMFT: '/imft', 'IMFT Supervisor': '/imft', BMFT: '/bmft', 'BMFT Supervisor': '/bmft', Marketing: '/marketing', 'Sales Manager': '/marketing', 'Sales Staff': '/marketing', Finance: '/finance', Store: '/store', NOC: '/noc', HR: '/hr', 'General Manager': '/gm-dashboard' };
      navigate(ROLE_DEFAULT[user.role] || ROLE_DEFAULT[user.department] || '/operator', { replace: true });
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || !['Admin', 'SuperAdmin'].includes(user.role)) return;
    fetch(`${API}/workflow/unified-stats`).then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
    fetch(`${API}/dma/health`).then(r => r.json()).then(setDmaHealth).catch(() => setDmaHealth({ status: 'disconnected' }));
  }, [user]);

  if (!user || !['Admin', 'SuperAdmin'].includes(user.role)) return null;

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-[3px] border-[var(--md-primary)] border-t-transparent rounded-full animate-spin" /></div>;

  const d = data || {};
  const t = d.tickets || {};
  const dma = d.dma || {};
  const issueData = (t.by_issue || []).slice(0, 6).map(i => ({ name: i.issue, value: i.count }));
  const deptData = (t.by_department || []).map(i => ({ name: i.dept, value: i.count }));
  const ticketStatusCounts = t.status_counts || {};

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 flex items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-[var(--md-primary-container)] flex items-center justify-center flex-shrink-0">
          <CheckCircle size={24} className="text-[var(--md-primary)]" weight="fill" />
        </div>
        <div className="flex-1"><p className="text-[16px] font-medium">Welcome back, {user?.name}</p><p className="text-[12px] text-[var(--md-on-surface-variant)]">{user?.department} &middot; {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}</p></div>
        <div className="hidden sm:flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[var(--md-success)] animate-pulse" /><span className="text-[12px] text-[var(--md-success)] font-medium">{(dma.online_now || 0).toLocaleString()} Online</span></div>
      </div>

      {/* DMA Connection Status */}
      {dmaHealth && dmaHealth.status !== 'connected' && (
        <div className="flex items-center gap-3 p-3 bg-[#FFEBEE] border border-[#EF9A9A] rounded-xl">
          <Warning size={20} className="text-[#B71C1C] flex-shrink-0" weight="fill" />
          <div className="flex-1">
            <p className="text-[13px] font-medium text-[#B71C1C]">DMA Radius Server Disconnected</p>
            <p className="text-[11px] text-[#C62828]">Host: {dmaHealth.host || '103.154.241.250'} — {dmaHealth.message || 'Server unreachable. Customer data, search, and billing sync unavailable.'}</p>
          </div>
          <button onClick={() => { fetch(`${API}/dma/health`).then(r => r.json()).then(d => { setDmaHealth(d); if (d.status === 'connected') { window.location.reload(); } }); }} className="md3-outlined-btn text-[11px] py-1.5 px-3 border-[#EF9A9A] text-[#B71C1C] flex-shrink-0">Retry</button>
        </div>
      )}
      {dmaHealth && dmaHealth.status === 'connected' && (
        <div className="flex items-center gap-2 p-2.5 bg-[#E8F5E9] border border-[#A5D6A7] rounded-xl">
          <div className="w-2 h-2 rounded-full bg-[#006D3B]" />
          <p className="text-[11px] text-[#1B5E20]">DMA Radius: <span className="font-medium">Connected</span> — {dmaHealth.users?.toLocaleString()} users synced</p>
        </div>
      )}

      {/* Top KPIs - 4 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Total Customers" value={(dma.total_customers || 0).toLocaleString()} sub={`${(dma.active_customers || 0).toLocaleString()} active`} icon={Users} color="#0052CC" bg="#E3ECFA" onClick={() => navigate('/customers')} />
        <KpiCard label="Open Tickets" value={t.open || 0} sub={`${t.today || 0} today | ${t.critical || 0} critical`} icon={Headset} color={t.critical > 0 ? '#BA1A1A' : '#0052CC'} bg={t.critical > 0 ? '#FFDAD6' : '#E3ECFA'} onClick={() => navigate('/operator')} />
        <KpiCard label="Online Sessions" value={(dma.online_now || 0).toLocaleString()} sub="Radius live PPPoE" icon={Lightning} color="#7C4DFF" bg="#EDE7F6" />
        <KpiCard label="Revenue" value={`$${(d.finance?.total_revenue || 0).toLocaleString()}`} sub={`${d.finance?.pending_payments || 0} pending approvals`} icon={CurrencyDollar} color="#F57F17" bg="#FFF8E1" onClick={() => navigate('/finance')} />
      </div>

      <section className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm" aria-label="Service ticket lifecycle overview">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div><h2 className="text-[15px] font-medium">Service Ticket Status Overview</h2><p className="text-[11px] text-[var(--md-on-surface-variant)]">Live lifecycle counts. Select a status to open Ticket View with that filter.</p></div>
          <button onClick={() => navigate('/operator')} className="text-[11px] text-[var(--md-primary)] font-medium">View all tickets</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {TICKET_LIFECYCLE_STATUSES.map(status => (
            <button key={status} onClick={() => navigate(`/operator?status=${encodeURIComponent(status)}`)} className="rounded-lg border border-[var(--md-outline-variant)] px-2.5 py-2 text-left transition-all hover:shadow-sm hover:border-[var(--md-primary)]">
              <span className="block text-[10px] font-medium text-[var(--md-on-surface-variant)] truncate">{status}</span>
              <span className="block text-xl font-bold tabular-nums leading-tight" style={{ color: STATUS_CLR[status] }}>{ticketStatusCounts[status] || 0}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Workflow Status - 4 columns */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <WorkflowCard title="Service Ops" icon={ClipboardText} items={[
          { label: 'Total Tickets', value: t.total || 0 },
          { label: 'Today Completed', value: `${t.today_completed || 0}/${t.today || 0}` },
        ]} color="#0052CC" link="/service-ops" navigate={navigate} />
        <WorkflowCard title="NOC Center" icon={ShieldCheck} items={[
          { label: 'Active Tasks', value: d.noc?.pending || 0 },
          { label: 'Awaiting Review', value: d.noc?.awaiting_approval || 0 },
        ]} color="#7C4DFF" link="/noc" navigate={navigate} />
        <WorkflowCard title="Store" icon={Package} items={[
          { label: 'Low Stock Alerts', value: d.store?.low_stock || 0, warn: d.store?.low_stock > 0 },
          { label: 'Pending Requests', value: d.store?.pending_requests || 0 },
        ]} color="#00695C" link="/store" navigate={navigate} />
        <WorkflowCard title="HR & Teams" icon={ChartLineUp} items={[
          { label: 'IMFT Techs', value: '2 active' },
          { label: 'BMFT Techs', value: '2 active' },
        ]} color="#006D3B" link="/hr" navigate={navigate} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-[var(--md-outline-variant)] p-5 shadow-sm">
          <h3 className="text-[16px] font-medium mb-3">Tickets by Issue Type</h3>
          {issueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={issueData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--md-outline-variant)" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'var(--md-on-surface-variant)' }} />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--md-on-surface-variant)' }} width={80} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid var(--md-outline-variant)', fontSize: 12 }} />
                <Bar dataKey="value" fill="#0052CC" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-[13px] text-[var(--md-on-surface-variant)] text-center py-12">No ticket data yet. Create tickets from Operator module.</p>}
        </div>
        <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-5 shadow-sm">
          <h3 className="text-[16px] font-medium mb-2">Team Distribution</h3>
          {deptData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart><Pie data={deptData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value" paddingAngle={3}>{deptData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">{deptData.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-[12px]"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} /><span className="text-[var(--md-on-surface-variant)]">{s.name}</span></div><span className="font-bold tabular-nums">{s.value}</span></div>
              ))}</div>
            </>
          ) : <p className="text-[13px] text-[var(--md-on-surface-variant)] text-center py-12">Assign tickets to teams to see distribution.</p>}
        </div>
      </div>

      {/* Workflow Pipeline */}
      <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-5 shadow-sm">
        <h3 className="text-[16px] font-medium mb-4">Operations Workflow Pipeline</h3>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {[
            { label: 'Customer Call', icon: Headset, module: 'Operator', link: '/operator' },
            { label: 'Ticket Created', icon: ClipboardText, module: 'Tickets', count: t.total || 0 },
            { label: 'Service Ops', icon: Wrench, module: 'IMFT / Service', count: t.open || 0, link: '/service-ops' },
            { label: 'NOC Center', icon: ShieldCheck, module: 'BMFT / NOC', count: d.noc?.pending || 0, link: '/noc' },
            { label: 'Field Work', icon: Path, module: 'IMFT + BMFT', link: '/imft' },
            { label: 'Approval', icon: CheckCircle, module: 'Supervisor', count: d.noc?.awaiting_approval || 0 },
            { label: 'Billing', icon: CreditCard, module: 'Finance', count: d.finance?.pending_payments || 0, link: '/finance' },
            { label: 'Completed', icon: CheckCircle, module: 'Done', count: t.today_completed || 0 },
          ].map((step, i, arr) => (
            <React.Fragment key={i}>
              <button onClick={() => step.link && navigate(step.link)} className={`flex-shrink-0 w-[130px] p-3 rounded-xl border border-[var(--md-outline-variant)] text-center hover:bg-[var(--md-surface-1)] transition-colors ${step.link ? 'cursor-pointer' : ''}`}>
                <step.icon size={20} className="mx-auto text-[var(--md-primary)] mb-1" weight="duotone" />
                <p className="text-[11px] font-medium">{step.label}</p>
                <p className="text-[9px] text-[var(--md-on-surface-variant)]">{step.module}</p>
                {step.count !== undefined && <p className="text-[14px] font-bold tabular-nums text-[var(--md-primary)] mt-0.5">{step.count}</p>}
              </button>
              {i < arr.length - 1 && <CaretRight size={16} className="text-[var(--md-outline)] flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--md-outline-variant)] flex items-center justify-between">
            <div className="flex items-center gap-2"><Headset size={16} className="text-[var(--md-primary)]" /><h4 className="text-[14px] font-medium">Recent Tickets</h4></div>
            <button onClick={() => navigate('/operator')} className="text-[11px] text-[var(--md-primary)] font-medium">View All</button>
          </div>
          {(d.recent_tickets || []).length === 0 ? <p className="p-6 text-center text-[12px] text-[var(--md-on-surface-variant)]">No tickets yet</p> :
          (d.recent_tickets || []).map((t, i) => (
            <div key={i} className="px-4 py-3 border-b border-[var(--md-outline-variant)] last:border-0 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] font-medium truncate">{t.customer} <span className="text-[var(--md-on-surface-variant)] font-normal">- {t.issue}</span></p>
                <p className="text-[10px] text-[var(--md-on-surface-variant)]">{t.id} | {t.team || 'Unassigned'}</p>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: STATUS_CLR[t.status] || '#666', background: `${STATUS_CLR[t.status] || '#666'}15` }}>{t.status}</span>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--md-outline-variant)] flex items-center justify-between">
            <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#7C4DFF]" /><h4 className="text-[14px] font-medium">NOC Tasks</h4></div>
            <button onClick={() => navigate('/noc')} className="text-[11px] text-[var(--md-primary)] font-medium">View All</button>
          </div>
          {(d.recent_noc || []).length === 0 ? <p className="p-6 text-center text-[12px] text-[var(--md-on-surface-variant)]">No NOC tasks yet</p> :
          (d.recent_noc || []).map((t, i) => (
            <div key={i} className="px-4 py-3 border-b border-[var(--md-outline-variant)] last:border-0 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] font-medium truncate">{t.type} <span className="text-[var(--md-on-surface-variant)] font-normal">- {t.location}</span></p>
                <p className="text-[10px] text-[var(--md-on-surface-variant)]">{t.id}</p>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: STATUS_CLR[t.status] || '#666', background: `${STATUS_CLR[t.status] || '#666'}15` }}>{t.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color, bg, onClick }) {
  return (
    <div onClick={onClick} className={`bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 h-[110px] flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center justify-between"><p className="text-[12px] font-medium text-[var(--md-on-surface-variant)] uppercase tracking-wider">{label}</p><div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bg }}><Icon size={18} weight="fill" style={{ color }} /></div></div>
      <div><p className="text-[28px] font-bold tabular-nums leading-none" style={{ color }}>{value}</p>{sub && <p className="text-[12px] text-[var(--md-on-surface-variant)] mt-1">{sub}</p>}</div>
    </div>
  );
}

function WorkflowCard({ title, icon: Icon, items, color, link, navigate }) {
  return (
    <button onClick={() => navigate(link)} className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm hover:shadow-md transition-shadow text-left w-full">
      <div className="flex items-center gap-2 mb-3"><Icon size={18} weight="fill" style={{ color }} /><h4 className="text-[13px] font-medium">{title}</h4><ArrowRight size={14} className="text-[var(--md-outline)] ml-auto" /></div>
      {items.map((item, i) => (
        <div key={i} className="flex justify-between items-center py-1.5 text-[12px]">
          <span className="text-[var(--md-on-surface-variant)]">{item.label}</span>
          <span className={`font-bold tabular-nums ${item.warn ? 'text-[#BA1A1A]' : ''}`} style={!item.warn ? { color } : {}}>{item.value}</span>
        </div>
      ))}
    </button>
  );
}
